import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { NO_DATA_TEXT } from "../constants";

export const copyObject = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

export const getUniqueKey = () => {
  return uuidv4();
};

export const capitalize = (value) => {
  if (typeof value !== "string") return value;

  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const sumFromListOfObjects = (obj, key) => {
  if (obj.length) {
    const sum = obj.reduce((prev, current) => ({
      [key]: prev[key] + current[key],
    }));
    return Math.round(sum[key]);
  }
  return 0;
};

export const sumFromList = (arr) => arr.reduce((prev, current) => prev + current, 0);

export const getPercent = (value, total, decimals = 0) => {
  if (value && total) {
    return ((value / total) * 100).toFixed(decimals);
  }
  return 0;
};

export const requestDateFormat = (date) => {
  return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
};

export const randomInt = (max, min = 0) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const getQuarter = (date) => {
  date = date || new Date();
  return Math.ceil((date.getMonth() + 1) / 3);
};

export const getYear = (date) => {
  date = date || new Date();
  return date.getFullYear();
};

export const getCurrencySymbol = (currencyCode) => {
  const currency = currencyCode ?? "USD";

  return Intl.NumberFormat("en", { style: "currency", currency })
    .formatToParts(0)
    .find((x) => x.type === "currency").value;
};

export const currencyFormatter = (num, currency = "$") => {
  const sign = num < 0 ? "-" : "";
  const formattedValue = nFormatter(Math.abs(num));

  return `${sign}${currency}${formattedValue}`;
};

// https://stackoverflow.com/a/9462382/7003620
export const nFormatter = (num) => {
  const lookup = [
    { value: 0, symbol: "" },
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "K" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
  ];

  return formatter(num, lookup);
};

export const nKilowattFormatter = (num, suffix = "h") => {
  const lookup = [
    { value: 1, symbol: ` kW${suffix}` },
    { value: 1e3, symbol: ` MW${suffix}` },
    { value: 1e6, symbol: ` GW${suffix}` },
  ];

  return formatter(num, lookup);
};

export const nCO2Formatter = (num) => {
  const lookup = [
    { value: 1, symbol: " kgCO2" },
    { value: 1e3, symbol: " tCO2" },
  ];
  return formatter(num, lookup);
};

const formatter = (num, lookup) => {
  if (typeof num === "number" && num > 0 && num < 1) {
    return num.toFixed(1);
  }

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return Math.abs(num) >= item.value;
    });

  if (typeof num === "number" && num) {
    let value = num / item.value;

    let digits = 0;
    // less then 1k
    if (num < 999) {
      let decimals = num.toString().split(".")[1];
      digits = decimals && decimals.length > 1 ? 1 : 0;
      // Here value can be 1k, 1M, 1B
    } else if (value < 10) {
      digits = 1;
    }

    return (
      value
        .toFixed(digits)
        .replace(rx, "$1")
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + item.symbol
    );
  }

  return "0";
};

export const getLastBarTotal = (data, fields) => {
  let sum = 0;
  if (data.length) {
    const lastItem = data[data.length - 1];
    fields.forEach((field) => (sum += lastItem[field]));
  }
  return +sum.toFixed();
};

export const generateEstimatedData = (data, lastBarTotal, isDataPerMonth, unit, max = 12) => {
  const date = data.length ? new Date(data[data.length - 1].date) : new Date();
  const length = data.length;
  if (length < max) {
    const count = max - data.length;
    return Array(count)
      .fill()
      .map((x, i) => {
        const d = isDataPerMonth
          ? `${i + 1 + length}-${date.getUTCFullYear()}E*`
          : `${date.getUTCFullYear() + 1 + i}E*`;
        return {
          x: d,
          estimated: lastBarTotal * (1 + (i + 2) / 10),
          unit,
        };
      });
  }
  return [];
};

export const isResponseDataPerMonth = (data) => {
  let isPerMonth = true;
  if (data.length > 1) {
    let firstItemDate = new Date(data[0].date);
    let secondItemDate = new Date(data[1].date);
    if (firstItemDate.getUTCFullYear() + 1 === secondItemDate.getUTCFullYear()) {
      isPerMonth = false;
    }
  }
  return isPerMonth;
};

export const getDays = (startDate, endDate) => {
  const date1 = new Date(startDate);
  const date2 = new Date(endDate);
  const diffTime = Math.abs(date2 - date1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const getLabel = (label) => {
  if (!label) return "";
  const value = label.replaceAll("_", " ");
  if (value.toLowerCase() === "ev") return value.toUpperCase();
  return value;
};

export const getDateFromNow = (offset, unit) => {
  return moment()
    .add(offset || 0, unit)
    .format("YYYY-MM-DD");
};

export const getYearOffsetFromNow = (year) => {
  if (!year) return undefined;
  return new Date(year).getFullYear() - new Date().getFullYear();
};

export const extractErrorMessage = (data) => {
  const messages = [];
  for (const match of JSON.stringify(data).matchAll(/"(\w+)":\["([\w\\.\s]+)\."/g)) {
    messages.push(match[2].replace("This field", match[1]));
  }
  return messages.join(". ");
};

// https://stackoverflow.com/a/18650828/7003620
export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatDatetime = (d, formatString) => {
  return d ? moment(d).format(formatString) : "";
};

export const floatFormatter = (num) => {
  if (!isNaN(num) && !Number.isInteger(+num)) {
    return +num.toFixed(1).toString().replace(/\.0$/, "");
  }
  return num;
};

const isEmptyValue = (v) => {
  return v === null || v === "";
};

export const validateObject = (object, requiredAttrs) => {
  for (const attr of requiredAttrs) {
    if (typeof attr === "string") {
      if (isEmptyValue(object[attr])) return false;
    } else {
      for (const key of Object.keys(attr)) {
        if (isEmptyValue(object[key])) return false;
        for (const subkey of attr[key]) {
          if (isEmptyValue(object[key][subkey])) return false;
        }
      }
    }
  }
  return true;
};

export const extractAddressInfo = (address) => {
  const addressComponents = address?.address_components;
  let country, postalCode;
  if (addressComponents?.length) {
    addressComponents.forEach((addressComponent) => {
      if (addressComponent?.types?.includes("country")) {
        country = addressComponent.long_name;
      } else if (addressComponent?.types?.includes("postal_code")) {
        postalCode = addressComponent.long_name;
      }
    });
  }
  return { country, postalCode, address: address.formatted_address };
};

export const getChartTicks = (chartData, tickCount) => {
  const step = Math.floor(chartData.length / tickCount);
  if (step === 0) return [];
  return chartData.reduce(
    (acc, e, i) => (i % step == 0 || i === chartData.length - 1 ? [...acc, e.x] : acc),
    []
  );
};

export const groupConsecutiveIndexXAxis = (arr) => {
  /*
   * Group consecutive numbers from array
   * Example:
   * arr = [1, 2, 3, 6, 8, 9, 10, 11]
   *
   * We'll get
   * [[1, 2, 3], [8, 9, 10, 11]]
   */
  var results = [];
  var limit = arr.length;
  let group = [arr[0]];
  let next;

  arr.forEach((_, i) => {
    if (arr[i] - arr[i - 1] !== 1) {
      group = [arr[i]];
      return;
    }

    group.push(arr[i]);

    if (i + 1 !== limit) {
      next = arr[i + 1];
    }

    if (next - arr[i] !== 1) {
      results.push(group);
    }
  });

  return results;
};

export const groupEmptyIndexXAxis = (arr) => {
  /*
   * Group zero index from array
   * Example:
   * arr = [1, 2, 0, 0, 8, 0, 0, 0]
   *
   * We'll get indexes
   * [[3, 4], [6, 7, 8]]
   */
  arr.push(null);
  var results = [];
  let group = [];
  let next;

  arr.forEach((_, i) => {
    if (arr[i] !== 0) {
      group = [];
      return;
    }

    group.push(i);

    if (i + 1 !== arr.length) {
      next = arr[i + 1];
    }

    if (next !== 0) {
      results.push(group);
    }
  });

  return results;
};

export const generateProfitabilityData = (results) => {
  let year;
  let isDateAssigned = false;
  const economicXAxis = [];
  const physicalXAxis = [];
  const partials = [];

  const chartData = results.map((result, i) => {
    const date = new Date(result.date);
    if (year !== getYear(date)) {
      year = getYear(date);
      isDateAssigned = true;
    }
    const data = {
      date: formatDatetime(date, "YYYY-MM"),
      consumption: result.income_sum,
      index: i,
    };
    if (year && isDateAssigned) data["x"] = year;
    isDateAssigned = false;

    if (result.is_partial && result.income_sum !== 0) {
      data["name"] = `Partial ${result.common_status === "economic_vacancy" ? "Economic" : "Physical"
        }\nvacancy`;
      partials.push(i);
    }

    if (result.is_low_scale && result.income_sum > 0) {
      if (result.common_status === "economic_vacancy") {
        economicXAxis.push(i);
        physicalXAxis.push(null);
      } else if (result.common_status === "physical_vacancy") {
        economicXAxis.push(null);
        physicalXAxis.push(i);
      }
    } else {
      economicXAxis.push(null);
      physicalXAxis.push(null);
    }

    return data;
  });

  const economicXAxisGroups = groupConsecutiveIndexXAxis(economicXAxis);
  const physicalXAxisGroups = groupConsecutiveIndexXAxis(physicalXAxis);

  const emptyXAxisGroups = groupEmptyIndexXAxis(results.map((result) => result.income_sum));

  const xAxis = economicXAxisGroups.concat(physicalXAxisGroups).flat(1);
  partials.forEach((partial) => {
    if (xAxis.includes(partial)) delete chartData[partial].name;
  });

  return { chartData, economicXAxisGroups, physicalXAxisGroups, emptyXAxisGroups };
};

export const onlyZeroValuesInArray = (arr) => arr.every((v) => v === 0);

export const extractValuesFromArrayOfObjects = (arr) => {
  return arr.map((obj) => Object.values(obj)).flat(1);
};

export const onlyUniqueValuesInObject = (obj, value) => {
  const uniqueValues = [...new Set(Object.values(obj))];
  return uniqueValues.length === 1 && uniqueValues[0] === value;
};

export const onlyUniqueValuesOfKeyInArray = (arr, key, value) => {
  const uniqueValues = [...new Set(arr.map((obj) => obj[key]))];
  return uniqueValues.length === 1 && uniqueValues[0] === value;
};

export const pluralize = (
  count,
  singular,
  { plural = null, suffix = "s", withCount = true } = {}
) => {
  return `${withCount ? count : ""} ${count !== 1 ? (plural ? plural : singular + suffix) : singular
    }`;
};

export const validateEmail = (email) => {
  var re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const isValid = re.test(String(email).toLowerCase());
  return isValid;
};

export const avgFromArrayOfNumbers = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

export const yearFromMonths = (months) => Math.floor(months / 12);

export const dmyUnit = (value, prefix) => {
  if (value.includes("d")) {
    return `${prefix} ${pluralize(+value.replace("d", ""), "Day")}`;
  } else {
    value = +value.replace("m", "");
  }
  return value < 13 ? `${prefix} ${value} Mon` : `${yearFromMonths(value)}y`;
};

export const UNITS_FILTERS = (unitSystem) => {
  return [
    {
      title: "BY TENANCY STATUS",
      key: "tenancy_status",
      options: [
        { key: "any", name: "Any" },
        { key: "RENTED", name: "Rented" },
        { key: "ECONOMIC_VACANCY", name: "Eco. vacant" },
        { key: "PHYSICAL_VACANCY", name: "Phys. vacant" },
      ],
      isOpen: true,
    },
    {
      title: "BY EXP. DATE",
      key: "leasing_end",
      options: [
        { key: "any", name: "Any" },
        { key: "this_q", name: "This Q" },
        { key: "this_year", name: "This year" },
      ],
      isOpen: true,
    },
    {
      title: "BY ELECT. STATUS",
      key: "electricity_status",
      options: [
        { key: "any", name: "Any" },
        { key: "CONNECTED", name: "Connected" },
        { key: "READY", name: "Ready to connect" },
        { key: "STAND_BY", name: "Stand by" },
        { key: "PENDING", name: "Pending" },
        { key: "MISSING", name: "Missing" },
        { key: "DONT_WANT_TO_CONNECT", name: "Don't want to connect" },
      ],
      isOpen: true,
    },
    {
      title: `BY AREA ${unitSystem.toUpperCase()}`,
      key: "area",
      options: [
        { key: "any", name: "Any" },
        { key: "area_large", name: `Over ${unitSystem === "sqm" ? "40" : "5,000"}` },
        {
          key: "area_mid",
          name: `${unitSystem === "sqm" ? "30" : "2,500"}-${unitSystem === "sqm" ? "40" : "5,000"}`,
        },
        { key: "area_small", name: `<${unitSystem === "sqm" ? "30" : "2,500"}` },
      ],
      isOpen: true,
    },
  ];
};

export const TENANTS_FILTERS = (unitSystem) => {
  return [
    {
      title: "BY ELECT. STATUS",
      key: "electricity_status",
      options: [
        { key: "any", name: "Any" },
        { key: "CONNECTED", name: "Connected" },
        { key: "READY", name: "Ready to connect" },
        { key: "STAND_BY", name: "Stand-by" },
        { key: "PENDING", name: "Pending" },
        { key: "MISSING", name: "Missing" },
        { key: "DONT_WANT_TO_CONNECT", name: "Don't want to connect" },
      ],
      isOpen: true,
    },
    {
      title: "BY EXP. DATE",
      key: "leasing_end",
      options: [
        { key: "any", name: "Any" },
        { key: "this_q", name: "This Q" },
        { key: "this_year", name: "This year" },
      ],
      isOpen: true,
    },
    {
      title: `BY RENTED ${unitSystem.toUpperCase()}`,
      key: "square",
      options: [
        { key: "any", name: "Any" },
        { key: "square_large", name: `Over ${unitSystem === "sqm" ? "40" : "5,000"}` },
        {
          key: "square_mid",
          name: `${unitSystem === "sqm" ? "30" : "2,500"}-${unitSystem === "sqm" ? "40" : "5,000"}`,
        },
        { key: "square_small", name: `<${unitSystem === "sqm" ? "30" : "2,500"}` },
      ],
      isOpen: true,
    },
  ];
};

export const UNITS_FILTERS_ALL_BUILDINGS = (unitSystem) => {
  return [
    {
      title: "FILTER BY STATUS",
      key: "status",
      options: [
        { key: "any", name: "Any" },
        { key: "RENTED", name: "Rented" },
        { key: "ECONOMIC_VACANCY", name: "Eco. vacant" },
        { key: "PHYSICAL_VACANCY", name: "Phys. vacant" },
      ],
    },
    {
      title: "FILTER BY EXP. DATE",
      key: "expired_date",
      options: [
        { key: "any", name: "Any" },
        { key: "this_q", name: "This Q" },
        { key: "this_year", name: "This year" },
      ],
    },
    {
      title: `FILTER BY ${unitSystem.toUpperCase()}`,
      key: "square",
      options: [
        { key: "any", name: "Any" },
        { key: "sqft_large", name: `Over ${unitSystem === "sqm" ? "40" : "5,000"}` },
        {
          key: "sqft_mid",
          name: `${unitSystem === "sqm" ? "30" : "2,500"}-${unitSystem === "sqm" ? "40" : "5,000"}`,
        },
        { key: "sqft_small", name: `<${unitSystem === "sqm" ? "30" : "2,500"}` },
      ],
    },
  ];
};

export const TENANTS_FILTERS_ALL_BUILDINGS = (unitSystem) => {
  return [
    {
      title: "FILTER BY STATUS",
      key: "status",
      options: [
        { key: "any", name: "Any" },
        { key: "CONNECTED", name: "Connected" },
        { key: "READY_TO_CONNECT", name: "Ready to connect" },
        { key: "STAND_BY", name: "Standby" },
        { key: "PENDING", name: "Pending" },
      ],
    },
    {
      title: "FILTER BY EXP. DATE",
      key: "expired_date",
      options: [
        { key: "any", name: "Any" },
        { key: "this_year", name: "This year" },
        { key: "longer", name: "Longer" },
      ],
    },
    {
      title: `FILTER BY RENTAL ${unitSystem.toUpperCase()}`,
      key: "square",
      options: [
        { key: "any", name: "Any" },
        { key: "sqft_large", name: `Over ${unitSystem === "sqm" ? "40" : "5,000"}` },
        {
          key: "sqft_mid",
          name: `${unitSystem === "sqm" ? "30" : "2,500"}-${unitSystem === "sqm" ? "40" : "5000"}`,
        },
        { key: "sqft_small", name: `<${unitSystem === "sqm" ? "30" : "2,500"}` },
      ],
    },
  ];
};

export const getConditionalText = (condition, valueText, noDataText = NO_DATA_TEXT) => {
  return condition ? valueText : noDataText;
};

export const isNotNull = (value) => value !== null;

export const isObjectEmpty = (obj) => Object.keys(obj).length === 0;

export const percentValue = (total, percent) => {
  return (total / 100) * percent;
};

export const opacityHexColor = (color, opacity) => `${color}${opacity}`;

export const isFloatOrInteger = (n) => {
  return parseFloat(n.match(/^-?\d*(\.\d+)?$/)) > 0;
};
